import axios from "axios";
import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  notifyAuthSessionChanged,
  saveAuthSession,
} from "../services/authSession";

const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * `authClient` talks to the CAS auth service (REACT_APP_AUTH_BASE_URL) —
 * login, token refresh, anything auth-related.
 *
 * `apiClient` talks to the TraceSync API (REACT_APP_API_BASE_URL) and is
 * what every feature service module (batchApi, etc.) should use. It
 * automatically attaches the bearer token + tenant id, retries once on a
 * 401 after refreshing the token, and normalizes errors into a consistent
 * shape so calling code doesn't need to know it's Axios underneath.
 */
export const authClient = axios.create({
  baseURL: AUTH_BASE_URL,
});

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  const tenantId = getTenantId();

  config.headers = config.headers || {};
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Assumption: the TraceSync API expects the tenant on an `X-Tenant-Id`
  // header (the brief only says "wherever required by the API" without
  // specifying the mechanism, and the Swagger host isn't reachable from
  // this environment to confirm). If the real API instead expects a query
  // param or a differently-named header, this is the only place to change.
  if (tenantId) {
    config.headers["X-Tenant-Id"] = tenantId;
  }

  return config;
});

/** Normalizes both Axios errors and successful-but-empty responses into a
 * single shape `{ status, message, data }` so UI code has one error
 * contract to handle regardless of what failed. */
const normalizeError = (error) => {
  if (axios.isCancel(error)) {
    return { status: 0, message: "Request cancelled.", data: null, cancelled: true };
  }
  if (error.response) {
    return {
      status: error.response.status,
      message:
        error.response.data?.message ||
        error.response.data?.detail ||
        error.response.statusText ||
        "Something went wrong.",
      data: error.response.data,
    };
  }
  if (error.request) {
    return { status: 0, message: "Unable to reach the server. Check your connection.", data: null };
  }
  return { status: -1, message: error.message || "Unexpected error.", data: null };
};

let refreshPromise = null;

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  const tenantId = getTenantId();

  if (!refreshToken || !tenantId) {
    clearAuthSession();
    notifyAuthSessionChanged();
    return false;
  }

  try {
    const params = new URLSearchParams({ tenant_id: tenantId });
    const response = await authClient.post(`cas/refresh-token?${params.toString()}`, { refreshToken });

    if (response.data?.jwtToken && response.data?.refreshToken) {
      saveAuthSession(response.data);
      notifyAuthSessionChanged();
      return true;
    }
  } catch {
    // fall through to session clear below
  }

  clearAuthSession();
  notifyAuthSessionChanged();
  return false;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest?._retried) {
      originalRequest._retried = true;

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      const didRefresh = await refreshPromise;
      if (didRefresh) {
        return apiClient(originalRequest);
      }
    }

    return Promise.reject(normalizeError(error));
  }
);

export default apiClient;
